/**
 * Customers Management Page - Admin interface for multi-tenant customer management
 */

import { useState, useEffect } from 'react';
import { authService, AuthUser } from '../services/auth-service';
import { logger } from '../utils/logger';
import axios from 'axios';
import ConfirmationModal from '../components/ConfirmationModal';
import { SearchInput } from '../components/SearchInput';
import { Customer, ProjectInstance } from '../types';
import { Button, Spinner, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Input, Textarea, Label } from '@/components/ui';

const API_BASE = `${import.meta.env?.VITE_API_URL || '/api'}/v1`;

export default function CustomersPage() {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [_customerProjects, setCustomerProjects] = useState<Map<string, ProjectInstance[]>>(new Map());

  // Form state
  const [formData, setFormData] = useState({
    customerName: '',
    contactEmail: '',
    contactName: '',
    address: '',
    phone: '',
  });

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    checkAuth();
    loadCustomers();
  }, []);

  const checkAuth = async () => {
    try {
      const user = await authService.getCurrentUser();
      setCurrentUser(user);
      
      if (!user || !user.groups?.includes('Administrators')) {
        setError('Administrator access required');
      }
    } catch (_err) {
      setError('Not authenticated');
    }
  };

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const tokens = await authService.getTokens();
      const response = await axios.get(`${API_BASE}/customers`, {
        headers: {
          Authorization: `Bearer ${tokens?.idToken}`,
        },
      });
      setCustomers(response.data.data || response.data);
      setError(null);
    } catch (err: unknown) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerProjects = async (customerId: string) => {
    try {
      const tokens = await authService.getTokens();
      const response = await axios.get(`${API_BASE}/customers/${customerId}/projects`, {
        headers: {
          Authorization: `Bearer ${tokens?.idToken}`,
        },
      });
      const data = response.data.data || response.data;
      setCustomerProjects(prev => new Map(prev).set(customerId, data.projects || []));
    } catch (err: unknown) {
      logger.error(`Failed to load projects for customer ${customerId}:`, err);
    }
  };

  const handleCreateCustomer = async () => {
    try {
      const tokens = await authService.getTokens();
      await axios.post(`${API_BASE}/customers`, formData, {
        headers: {
          Authorization: `Bearer ${tokens?.idToken}`,
        },
      });
      setShowCreateModal(false);
      resetForm();
      loadCustomers();
    } catch (err: unknown) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;
    
    try {
      const tokens = await authService.getTokens();
      await axios.patch(`${API_BASE}/customers/${editingCustomer.customerId}`, formData, {
        headers: {
          Authorization: `Bearer ${tokens?.idToken}`,
        },
      });
      setEditingCustomer(null);
      resetForm();
      loadCustomers();
    } catch (err: unknown) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!selectedCustomer) return;
    
    try {
      const tokens = await authService.getTokens();
      await axios.delete(`${API_BASE}/customers/${selectedCustomer.customerId}`, {
        headers: {
          Authorization: `Bearer ${tokens?.idToken}`,
        },
      });
      setShowDeleteModal(false);
      setSelectedCustomer(null);
      loadCustomers();
    } catch (err: unknown) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      contactEmail: '',
      contactName: '',
      address: '',
      phone: '',
    });
  };

  const openEditModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      customerName: customer.customerName,
      contactEmail: customer.contactEmail || '',
      contactName: customer.contactName || '',
      address: customer.address || '',
      phone: customer.phone || '',
    });
  };

  const openDeleteModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowDeleteModal(true);
    // Load projects to show warning
    loadCustomerProjects(customer.customerId);
  };

  const filteredCustomers = customers.filter(c => 
    c.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.customerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.contactEmail || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (error && !currentUser?.groups?.includes('Administrators')) {
    return (
      <div className="p-8">
        <div className="bg-destructive-light border border-destructive rounded-lg p-4 text-destructive">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Customers</h1>
          <p className="text-muted-foreground">
            Manage customer organizations for multi-tenant isolation
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <span>+</span>
          <span>Add Customer</span>
        </Button>
      </div>

      {error && (
        <div className="mb-4 bg-destructive-light border border-destructive rounded-lg p-4 text-destructive">
          {error}
          <Button onClick={() => setError(null)} variant="ghost" size="sm" className="ml-2 text-destructive hover:text-destructive">
            Dismiss
          </Button>
        </div>
      )}

      <div className="mb-4">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search customers..."
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="xl" className="text-link" />
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Customer ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                    {searchTerm ? 'No customers match your search' : 'No customers found. Create one to get started.'}
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr key={customer.customerId} className="hover:bg-muted">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-foreground">
                        {customer.customerName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-foreground">
                        {customer.contactName || '-'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {customer.contactEmail || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {customer.customerId}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(customer.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        onClick={() => openEditModal(customer)}
                        variant="ghost"
                        size="sm"
                        className="text-link hover:text-link-hover mr-2"
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => openDeleteModal(customer)}
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog 
        open={showCreateModal || !!editingCustomer} 
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false);
            setEditingCustomer(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? 'Edit Customer' : 'Create New Customer'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Customer Name *</Label>
              <Input
                id="customerName"
                type="text"
                value={formData.customerName}
                onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                placeholder="e.g., Mammoth Freighters"
                disabled={!!editingCustomer}
              />
              {editingCustomer && (
                <p className="text-xs text-muted-foreground">Customer name cannot be changed</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactName">Contact Name</Label>
              <Input
                id="contactName"
                type="text"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                placeholder="e.g., John Smith"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contactEmail">Contact Email</Label>
              <Input
                id="contactEmail"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                placeholder="e.g., john@mammoth.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="e.g., +1 555-123-4567"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={2}
                placeholder="Street address, city, state, ZIP"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                setEditingCustomer(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={editingCustomer ? handleUpdateCustomer : handleCreateCustomer}
              disabled={!formData.customerName.trim()}
            >
              {editingCustomer ? 'Save Changes' : 'Create Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCustomer && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedCustomer(null);
          }}
          onConfirm={handleDeleteCustomer}
          title="Delete Customer"
          message={`Are you sure you want to delete "${selectedCustomer.customerName}"? This action cannot be undone.`}
          confirmLabel="Delete"
          confirmStyle="danger"
        />
      )}
    </div>
  );
}
